import './demand-quote-item.css';

import { uiColor } from '@kerfjs/ui/css-values';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { Text } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';
import { Trophy } from 'lucide';

import type { QuoteMatrix } from '../data/api.js';
import { formatCurrencyText } from '../lib/currency-format.js';
import { CurrencyAmount } from './currency-amount.js';
import { LINE_ITEM_SECTION_VALUES_FLEX, LineItemSectionLabel } from './line-item-section-label.js';

/** The best (lowest) quote price to highlight, or null when there are 0-1 quotes
 *  to compare — the marker only appears with multiple quotes (HS2-TZM60Z). */
export function bestQuotePrice(prices: readonly { price: number }[]): number | null {
  return prices.length > 1 ? Math.min(...prices.map((p) => p.price)) : null;
}

export function DemandQuoteItem({
  matrix,
  lineId,
  chosenQuoteLineId = null,
  readOnly = false,
}: {
  matrix: QuoteMatrix | null;
  lineId: string;
  chosenQuoteLineId?: string | null;
  readOnly?: boolean;
}): SafeHtml {
  if (matrix === null) return <></>;
  const prices = matrix.columns.flatMap((column) => {
    const cell = matrix.cells[lineId]?.[column.quotationId];
    return cell?.basePrice == null
      ? []
      : [{ id: column.quotationId, quoteLineId: cell.quoteLineId, vendor: column.vendorName, price: cell.basePrice }];
  });
  if (prices.length === 0) return <></>;
  // Quote prices are per-unit (the vendor's unit price converted to the base
  // currency), so show the line's unit — "145 PKR/kg" — to make the relationship
  // to the line's quantity tag clear (HS2-W1VH12). Fall back to a bare amount
  // when the line has no unit.
  const unit = matrix.lineItems.find((li) => li.id === lineId)?.unit ?? null;
  const priceLabel = (value: number): string =>
    `${formatCurrencyText(value, matrix.base)}${unit !== null && unit !== '' ? `/${unit}` : ''}`;
  // Highlight the best (lowest-price) quote only when there's more than one to
  // compare (HS2-TZM60Z). Ties (rare) all get the marker.
  const best = bestQuotePrice(prices);
  const lowest = Math.min(...prices.map((quote) => quote.price));
  // The selected quote for the line (HS2-EKEE9S): the current decision's chosen
  // quote, or — as a default before any decision — the best one. It's outlined so
  // the reviewer can see at a glance which quote is (or would be) taken.
  const bestQuoteLineId = best !== null ? (prices.find((p) => p.price === best)?.quoteLineId ?? null) : null;
  const selectedQuoteLineId = chosenQuoteLineId ?? bestQuoteLineId;
  return (
    <Row className="demand-quote-item" vAlign="top" wrap>
      <LineItemSectionLabel label="Quotes" />
      <Row flex={LINE_ITEM_SECTION_VALUES_FLEX} wrap vAlign="middle" controlInsets="trbl">
        {prices.map((quote) => {
          const isBest = best !== null && quote.price === best;
          const isSelected = selectedQuoteLineId !== null && quote.quoteLineId === selectedQuoteLineId;
          // The full vendor name leads the tooltip, since a long one truncates (HS2-ZKRYMQ).
          const title = [quote.vendor, isSelected ? 'Selected quote' : '', isBest ? 'Best (lowest) quote' : '']
            .filter((t) => t !== '')
            .join(' · ');
          return (
            <button
              type="button"
              class="demand-quote-item-chip"
              data-best={String(isBest)}
              data-selected={String(isSelected)}
              data-key={quote.id}
              data-action="line-quote-approve"
              data-line-id={lineId}
              data-quote-line-id={quote.quoteLineId}
              data-quote-price={String(quote.price)}
              data-lowest={quote.price === lowest ? 'true' : 'false'}
              aria-label={`Approve ${quote.vendor} at ${priceLabel(quote.price)}`}
              disabled={readOnly}
              title={title}
            >
              {/* A long vendor name truncates through Kerf Text (HS2-ZKRYMQ, HS2-XHYEM2). The trophy
                  stays with the name and the unit with the price when a narrow chip wraps (HS2-SZ3ECF). */}
              <strong>
                {isBest ? (
                  <LucideIcon icon={Trophy} name="Best Quote" size={14} color={uiColor('success-on-quiet')} />
                ) : null}
                <Text variant="span" wrap="truncate">
                  {quote.vendor}
                </Text>
              </strong>{' '}
              <span class="demand-quote-item-price">
                <CurrencyAmount value={quote.price} currency={matrix.base} />
                {unit !== null && unit !== '' ? `/${unit}` : null}
              </span>
            </button>
          );
        })}
      </Row>
    </Row>
  );
}
